/**
 * BoMyPlanningPage — Vue enseignant : mes prochaines leçons + actions rapides.
 * Route : /bo/mon-planning
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Car, User, CheckCircle2, XCircle, AlertCircle, Plus, X, Sparkles } from 'lucide-react';
import api from '../../api';
import { BoLessonBilanModal } from './BoLessonBilanModal';

const BO_BLUE = '#1d4ed8';
const TYPE_LABEL = {
  conduite: 'Conduite',
  code: 'Code',
  examen: 'Examen',
  rvp: 'RVP',
  moto_plateau: 'Moto · Plateau',
};
const TYPE_COLOR = {
  conduite: '#059669',
  code: '#6366f1',
  examen: '#dc2626',
  rvp: '#f59e0b',
  moto_plateau: '#7c3aed',
};

export default function BoMyPlanningPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], linked: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [bilanFor, setBilanFor] = useState(null); // lesson en cours de bilan

  // Formulaire rapide — pré-rempli aux valeurs les plus utilisées
  const defaultDate = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [form, setForm] = useState({
    student_name: '',
    date: defaultDate,
    start_time: '14:00',
    duration_min: 60,
    type: 'conduite',
    vehicle_type: 'bm',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = 'Mon planning · Back-office';
    reload();
  }, []);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.boMyPlanning();
      setData(r);
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        navigate('/login', { state: { from: '/bo/mon-planning' } });
        return;
      }
      setError(e.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const doAction = async (fn, msg) => {
    try {
      await fn();
      setActionMsg(msg);
      reload();
      setTimeout(() => setActionMsg(null), 3000);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleComplete = (id, name) =>
    doAction(() => api.boPlanningComplete(id), `Leçon avec ${name} marquée comme effectuée`);

  // Ouvre le modal de bilan (complète leçon + bilan en 1 flow)
  const openBilan = (lesson) => setBilanFor(lesson);

  const handleCancel = (id, name) => {
    if (!window.confirm(`Annuler la leçon avec ${name} ?`)) return;
    return doAction(() => api.boPlanningCancel(id, 'annule'), `Leçon avec ${name} annulée`);
  };

  const handleAddLesson = async (e) => {
    e.preventDefault();
    if (!form.student_name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const r = await api.boPlanningAdd({
        ...form,
        student_name: form.student_name.trim(),
        duration_min: parseInt(form.duration_min, 10) || 60,
      });
      setShowAdd(false);
      setForm({ ...form, student_name: '', notes: '' }); // Garde date/heure/type pour ajout rapide en série
      setActionMsg(
        r.created_fiche
          ? `Leçon créée + nouvelle fiche élève "${r.student_name}" ajoutée`
          : `Leçon créée avec ${r.student_name}`
      );
      reload();
      setTimeout(() => setActionMsg(null), 4000);
    } catch (e) {
      setError(e.message || 'Impossible de créer la leçon');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="bo-my-planning">
      <Link to="/bo" className="inline-flex items-center gap-1.5 text-xs font-bold text-foreground/60 hover:text-foreground mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back-office
      </Link>

      <div className="text-[11px] font-black uppercase tracking-[0.22em] mb-1" style={{ color: BO_BLUE }}>
        Enseignant · Planning
      </div>
      <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-foreground mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        Mon planning
      </h1>

      {actionMsg && (
        <div className="rounded-lg border-2 border-emerald-500/40 bg-emerald-500/10 p-3 mb-4 text-xs text-foreground/80 flex items-center gap-2" data-testid="bo-planning-msg">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" /> {actionMsg}
        </div>
      )}

      {error && (
        <div className="rounded-lg border-2 border-red-500/40 bg-red-500/10 p-3 mb-4 flex items-start gap-2 text-xs" data-testid="bo-planning-error">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" /> {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-foreground/50 py-10 text-center">Chargement…</p>
      ) : !data.linked ? (
        <div className="rounded-xl border-2 border-dashed border-border p-8 text-center" data-testid="bo-planning-unlinked">
          <User className="w-8 h-8 mx-auto mb-3 text-foreground/40" />
          <p className="text-sm text-foreground/70 mb-2">
            Aucune fiche enseignant liée à votre email dans le secrétariat.
          </p>
          <p className="text-xs text-foreground/50">
            Demandez au secrétariat de créer votre fiche avec l&apos;email <strong>{data.email || '(votre email)'}</strong>,
            ou lancez un <strong>Seed démo</strong> depuis le back-office.
          </p>
        </div>
      ) : data.items.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-border p-8 text-center" data-testid="bo-planning-empty">
          <Calendar className="w-8 h-8 mx-auto mb-3 text-foreground/40" />
          <p className="text-sm text-foreground/60">
            Aucune leçon planifiée à venir. Utilisez le bouton <em>Ajouter une leçon</em> pour en créer une.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.items.map((l) => {
            const done = l.status === 'effectue';
            const cancelled = l.status === 'annule';
            const color = TYPE_COLOR[l.type] || BO_BLUE;
            return (
              <div
                key={l.id}
                className={`rounded-xl border-2 border-border bg-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 ${cancelled ? 'opacity-50 line-through' : ''}`}
                data-testid={`lesson-${l.id}`}
              >
                {/* Date */}
                <div className="flex-shrink-0 text-center px-3 py-2 rounded-lg" style={{ background: `${color}18` }}>
                  <div className="text-[9px] font-black uppercase tracking-widest" style={{ color }}>
                    {new Date(l.date).toLocaleDateString('fr-FR', { weekday: 'short' })}
                  </div>
                  <div className="text-lg font-black text-foreground leading-tight">
                    {new Date(l.date).getDate()}
                  </div>
                  <div className="text-[10px] text-foreground/60 font-black">{l.start_time}</div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest text-white"
                      style={{ background: color }}
                    >
                      {TYPE_LABEL[l.type] || l.type}
                    </span>
                    <span className="text-[10px] text-foreground/50 inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {l.duration_min} min
                    </span>
                    {l.vehicle_type && (
                      <span className="text-[10px] text-foreground/50 inline-flex items-center gap-1">
                        <Car className="w-3 h-3" /> {l.vehicle_type}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 font-black text-foreground text-sm sm:text-base">
                    {l.student_name || '(Élève inconnu)'}
                  </div>
                  {l.notes && (
                    <div className="mt-1 text-xs text-foreground/60 italic">{l.notes}</div>
                  )}
                </div>

                {/* Actions */}
                {!done && !cancelled && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => openBilan(l)}
                      className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg text-white text-[10px] font-black uppercase tracking-widest hover:opacity-90 transition"
                      style={{ background: BO_BLUE }}
                      title="Faire le bilan et marquer effectuée"
                      data-testid={`btn-bilan-${l.id}`}
                    >
                      <Sparkles className="w-3.5 h-3.5" /> Bilan
                    </button>
                    <button
                      onClick={() => handleComplete(l.id, l.student_name)}
                      className="p-2 rounded-lg border-2 border-emerald-500/40 hover:bg-emerald-500/10 transition"
                      title="Marquer effectuée sans bilan"
                      data-testid={`btn-complete-${l.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    </button>
                    <button
                      onClick={() => handleCancel(l.id, l.student_name)}
                      className="p-2 rounded-lg border-2 border-red-500/40 hover:bg-red-500/10 transition"
                      title="Annuler"
                      data-testid={`btn-cancel-${l.id}`}
                    >
                      <XCircle className="w-4 h-4 text-red-600" />
                    </button>
                  </div>
                )}
                {done && (
                  <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Effectuée
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Bouton flottant "Ajouter une leçon" — mobile-first FAB */}
      {data.linked && (
        <button
          onClick={() => setShowAdd(true)}
          className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full text-white text-sm font-black shadow-xl hover:opacity-90 transition-all active:scale-95"
          style={{ background: BO_BLUE, boxShadow: '0 10px 30px -5px rgba(29,78,216,0.4)' }}
          data-testid="btn-quick-add-lesson"
        >
          <Plus className="w-5 h-5" /> Ajouter une leçon
        </button>
      )}

      {/* Modal Ajout rapide */}
      {showAdd && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShowAdd(false)}
        >
          <form
            onSubmit={handleAddLesson}
            onClick={(e) => e.stopPropagation()}
            className="bg-card border-t-2 sm:border-2 border-border rounded-t-2xl sm:rounded-2xl max-w-md w-full p-5 sm:p-6 space-y-3 max-h-[90vh] overflow-y-auto"
            data-testid="modal-quick-add-lesson"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-foreground/50">
                  Ajout rapide
                </div>
                <h2 className="text-lg font-black" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Nouvelle leçon
                </h2>
              </div>
              <button type="button" onClick={() => setShowAdd(false)} data-testid="btn-close-quick-add">
                <X className="w-5 h-5 text-foreground/60" />
              </button>
            </div>

            {/* Élève */}
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">
                Élève * <span className="text-foreground/40 normal-case font-normal">(la fiche est créée si absente)</span>
              </span>
              <input
                autoFocus required
                value={form.student_name}
                onChange={(e) => setForm({ ...form, student_name: e.target.value })}
                placeholder="Prénom Nom"
                className="w-full mt-1 px-3 py-3 rounded-lg border-2 border-border bg-background text-base outline-none focus:border-[#1d4ed8]"
                data-testid="input-quick-student-name"
              />
            </label>

            {/* Date + Heure (grille 2 cols mobile) */}
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Date *</span>
                <input
                  type="date" required
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="w-full mt-1 px-2 py-3 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                  data-testid="input-quick-date"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Heure *</span>
                <input
                  type="time" required
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  className="w-full mt-1 px-2 py-3 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                  data-testid="input-quick-time"
                />
              </label>
            </div>

            {/* Type (pills) */}
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Type</span>
              <div className="mt-1.5 grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                {Object.entries(TYPE_LABEL).map(([k, v]) => {
                  const on = form.type === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setForm({ ...form, type: k, duration_min: k === 'moto_plateau' ? 120 : 60 })}
                      className={`text-[10px] font-black uppercase tracking-widest px-2 py-2 rounded-lg transition ${on ? 'text-white' : 'bg-muted text-foreground/60 hover:bg-muted/80'}`}
                      style={on ? { background: TYPE_COLOR[k] } : {}}
                      data-testid={`quick-type-${k}`}
                    >
                      {v.replace(' · ', '\n')}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Durée + Véhicule */}
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Durée (min)</span>
                <select
                  value={form.duration_min}
                  onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value, 10) })}
                  className="w-full mt-1 px-2 py-3 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                  data-testid="input-quick-duration"
                >
                  <option value={30}>30</option>
                  <option value={45}>45</option>
                  <option value={60}>60</option>
                  <option value={90}>90</option>
                  <option value={120}>120</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Véhicule</span>
                <select
                  value={form.vehicle_type}
                  onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
                  className="w-full mt-1 px-2 py-3 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8]"
                  data-testid="input-quick-vehicle"
                >
                  <option value="bm">Boîte manuelle</option>
                  <option value="ba">Boîte auto</option>
                  <option value="moto">Moto</option>
                  <option value="simulateur">Simulateur</option>
                </select>
              </label>
            </div>

            {/* Notes (repliable) */}
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-foreground/60">Notes (optionnel)</span>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="Ex : leçon de rattrapage, examen blanc…"
                className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-border bg-background text-sm outline-none focus:border-[#1d4ed8] resize-none"
                data-testid="input-quick-notes"
              />
            </label>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className="flex-1 px-3 py-3 rounded-lg border-2 border-border text-sm font-black hover:bg-muted"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving || !form.student_name.trim()}
                className="flex-[2] px-3 py-3 rounded-lg text-white text-sm font-black hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-1.5"
                style={{ background: BO_BLUE }}
                data-testid="btn-submit-quick-lesson"
              >
                <Plus className="w-4 h-4" /> {saving ? 'Création…' : 'Créer la leçon'}
              </button>
            </div>
            <p className="text-[10px] text-foreground/40 text-center pt-1">
              💡 Après création, la date et l&apos;heure restent pour un ajout rapide en série.
            </p>
          </form>
        </div>
      )}
      {/* Modal Bilan de leçon */}
      {bilanFor && (
        <BoLessonBilanModal
          lesson={bilanFor}
          onClose={() => setBilanFor(null)}
          onDone={(msg) => {
            setActionMsg(msg);
            reload();
            setTimeout(() => setActionMsg(null), 4000);
          }}
        />
      )}
    </div>
  );
}
